import { useEffect, useMemo, useRef, useState } from "react"
import _ from "lodash"
import moment from "moment"

import {
  deleteDistribuitorOrderItem,
  getClients,
  getDistribuitorClientDailyAvailable,
  postDistribuitorClientOrder,
  putDistribuitorClientOrderItem,
} from "helpers/backend_helper"

import { MESES, SAVED_FEEDBACK_MS } from "./static"

/** «21/JULIO/2026», el formato del título de la pantalla. */
export const tituloDelDia = fecha => {
  const dia = moment(fecha, "YYYY-MM-DD")
  if (!dia.isValid()) return ""
  return `${dia.format("DD")}/${MESES[dia.month()]}/${dia.format("YYYY")}`
}

/** «Mar 21 Jul 2026», el formato de los desplegables de fecha. */
export const etiquetaFecha = fecha => {
  const dia = moment(fecha, "YYYY-MM-DD")
  return dia.isValid() ? _.capitalize(dia.format("ddd DD MMM YYYY")) : fecha
}

export const fechaDeHoy = () => moment().format("YYYY-MM-DD")

/**
 * El día de hoy dentro de la ventana que manda el servidor. Si no aparece —zona
 * horaria distinta a la del backend, o ventana movida— se elige el día más
 * cercano sin pasarse, nunca el primero de la lista, que son 5 días atrás.
 */
export const fechaPorDefecto = dias => {
  const hoy = fechaDeHoy()
  if (!dias || dias.length === 0) return hoy
  if (dias.includes(hoy)) return hoy

  const previos = dias.filter(dia => dia <= hoy)
  return previos.length ? previos[previos.length - 1] : dias[0]
}

/**
 * Deja fuera las partidas canceladas: el endpoint las sigue devolviendo con
 * `cancelled_at`, y sin este filtro reaparecerían tras eliminarlas.
 */
export const partidasVivas = order =>
  (order?.items || []).filter(item => !item.cancelled_at)

export default function useOrdersCreate() {
  const [isLoading, setIsLoading] = useState(false)

  const [clients, setClients] = useState([])
  const [clientId, setClientId] = useState(null)

  const [dates, setDates] = useState([])
  // Arranca en hoy para que la cabecera muestre el día desde el primer render,
  // antes incluso de elegir cliente.
  const [date, setDate] = useState(fechaDeHoy)

  const [addresses, setAddresses] = useState([])
  const [addressId, setAddressId] = useState(null)

  const [data, setData] = useState([])

  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const savedTimer = useRef(null)

  const [editingItem, setEditingItem] = useState(null)

  useEffect(() => {
    const cargarClientes = async () => {
      setIsLoading(true)
      try {
        const response = await getClients({ is_for_order: true })
        setClients(
          _.orderBy(
            (response || []).filter(cliente => cliente.isActivated),
            ["company_name"]
          )
        )
      } catch (error) {
        // api_helper ya notifica el error
      }
      setIsLoading(false)
    }
    cargarClientes()

    return () => clearTimeout(savedTimer.current)
  }, [])

  const limpiarCliente = () => {
    setData([])
    setDates([])
    setDate(fechaDeHoy())
    setAddresses([])
    setAddressId(null)
    setEditingItem(null)
  }

  /** Trae la disponibilidad del cliente. Solo fija fecha y dirección en la primera carga. */
  const cargarDisponibilidad = async (id, inicial) => {
    setIsLoading(true)
    try {
      const response = await getDistribuitorClientDailyAvailable({
        enterprise_id: id,
      })

      setData(response?.data || [])

      if (inicial) {
        const dias = response?.days || []
        setDates(dias)
        setDate(fechaPorDefecto(dias))

        const direcciones = response?.enterprise?.deliveries_address || []
        setAddresses(direcciones)
        setAddressId(direcciones[0] ? direcciones[0].id : null)
      }
    } catch (error) {
      // api_helper ya notifica el error
      if (inicial) limpiarCliente()
    }
    setIsLoading(false)
  }

  const onChangeClient = async id => {
    setClientId(id)
    setEditingItem(null)

    if (!id) {
      limpiarCliente()
      return
    }

    await cargarDisponibilidad(id, true)
  }

  const onChangeDate = value => {
    setDate(value)
    setEditingItem(null)
  }

  const onChangeAddress = value => {
    setAddressId(value)
    setEditingItem(null)
  }

  const address = addresses.find(item => item.id === addressId) || null

  // Cada grupo es un proveedor + punto de distribución, ya recortado a la fecha
  // y a la zona de entrega elegidas. Memorizado porque el borrador de cada
  // tarjeta se reinicia cuando cambia la identidad de `rows`.
  const grupos = useMemo(
    () =>
      (data || [])
        .map(grupo => ({
          rows: _.orderBy(
            (grupo.rows || []).filter(
              row =>
                row.date === date &&
                (row.prices || []).some(
                  price =>
                    Number(price.region_id) === Number(address?.region_id)
                )
            ),
            ["product_position"],
            ["asc"]
          ),
          order:
            (grupo.orders || []).find(
              item =>
                item.order_date === date &&
                Number(item.address_id) === Number(address?.id)
            ) || null,
        }))
        // Un grupo sin producto disponible y sin partidas vivas no pinta nada.
        .filter(
          grupo =>
            grupo.rows.some(row => row.available_product > 0) ||
            partidasVivas(grupo.order).length > 0
        ),
    [data, date, address]
  )

  const sinProveedores = !!(address && data.length === 0)
  const sinProducto = data.length > 0 && grupos.length === 0

  const marcarGuardado = () => {
    setIsSaved(true)
    clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setIsSaved(false), SAVED_FEEDBACK_MS)
  }

  /** Alta de pedido para un proveedor. Devuelve true si el servidor lo aceptó. */
  const onCreateOrder = async payload => {
    if (isSaving || !clientId || !address || !date) return false
    setIsSaving(true)
    let ok = false
    try {
      await postDistribuitorClientOrder({
        ...payload,
        enterprise_id: clientId,
        date,
        address_id: address.id,
      })
      await cargarDisponibilidad(clientId, false)
      marcarGuardado()
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  /** Edición de una partida ya solicitada. */
  const onUpdateItem = async (itemId, cambios) => {
    if (isSaving || !clientId) return false
    setIsSaving(true)
    let ok = false
    try {
      await putDistribuitorClientOrderItem(itemId, {
        enterprise_id: clientId,
        quantity: cambios.quantity,
        observations: cambios.observations,
      })
      await cargarDisponibilidad(clientId, false)
      setEditingItem(null)
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  /** Cancelación lógica de una partida. Libera la disponibilidad del día. */
  const onDeleteItem = async itemId => {
    if (isSaving || !clientId) return false
    setIsSaving(true)
    let ok = false
    try {
      await deleteDistribuitorOrderItem(itemId)
      await cargarDisponibilidad(clientId, false)
      if (editingItem === itemId) setEditingItem(null)
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  return {
    state: {
      isLoading,
      clients,
      clientId,
      dates,
      date,
      addresses,
      addressId,
      address,
      grupos,
      sinProveedores,
      sinProducto,
      isSaving,
      isSaved,
      editingItem,
    },
    events: {
      onChangeClient,
      onChangeDate,
      onChangeAddress,
      onCreateOrder,
      onUpdateItem,
      onDeleteItem,
      onStartEdit: setEditingItem,
      onCancelEdit: () => setEditingItem(null),
    },
  }
}
